import { useNavigate } from "@tanstack/react-router";
import { GraduationCap } from "lucide-react";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { LibraryListView, useLibraryList } from "@/components/shared/library-list";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { OriginLink } from "@/components/shared/origin-link";
import { SkillCards } from "@/components/skills/SkillCards";
import { SkillFormDialog } from "@/components/skills/SkillFormDialog";
import { useSkills } from "@/hooks/use-skills";
import { carryListSearch } from "@/lib/list-search";

export function SkillsTable() {
  const navigate = useNavigate();
  const list = useLibraryList("skills.view");
  const result = useSkills(list.query, list.pageRequest);

  return (
    <LibraryListView
      list={list}
      result={result}
      noun={{ one: "skill", many: "skills" }}
      icon={GraduationCap}
      headers={[
        { label: "Name", sortKey: "name" },
        { label: "Role", sortKey: "roleName", className: "hidden sm:table-cell" },
        { label: "Status", sortKey: "status" },
      ]}
      renderCards={({ items, loading, empty }) => (
        <SkillCards skills={items} loading={loading} empty={empty} />
      )}
      renderCreateDialog={({ open, onOpenChange }) => (
        <SkillFormDialog open={open} onOpenChange={onOpenChange} />
      )}
      renderRow={(s) => (
        <tr
          key={s.id}
          {...interactiveRowProps(
            () =>
              navigate({
                to: "/dashboard/skills/$skillId",
                params: { skillId: s.id },
                search: carryListSearch(),
              }),
            `Open ${s.name}`,
          )}
        >
          <td className="px-4 py-2.5 font-medium text-foreground">{s.name}</td>
          <td className="hidden sm:table-cell px-4 py-2.5 text-muted-foreground">
            {s.roles.length === 0 ? (
              "—"
            ) : (
              <span className="line-clamp-1">
                {s.roles.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ", "}
                    <OriginLink
                      to="/dashboard/roles/$roleId"
                      params={{ roleId: r.id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary hover:underline"
                    >
                      {r.name}
                    </OriginLink>
                  </span>
                ))}
              </span>
            )}
          </td>
          <td className="px-4 py-2.5">
            <LifecycleBadge status={s.status} />
          </td>
        </tr>
      )}
    />
  );
}
